import { CommandEventTypes, CommandSource, ContentStatusEvents, ContentStatusManager, DataType, EventManager, ICommandData, IContentStatusManager, IEventManager, IMutationManager, IPresentationData, ISlideFileData, ITransactionProvider, IValidMutationData, IValidSetPagePropertiesMutationData, JSF32RandomIDGenerator, MutationManager, MutationSource, MutationType, ObserverManager, PresentationDataModel, PresentationEventTypes, STPageType, STSlideSizeType, SlideFile, TransactionManager, createMutations, createTransactionProvider, getPresentationKeyframe } from '@tencent/slide-sdk-model';
import { OperationLogger } from '@tencent/slide-sdk-devtools';
import { IPresentationStoreService } from '../interfaces/presentation-service.interface';
import { V3_DEVR } from './client-vars-service';
import { ISlideInitialDataService, SlideInitialDataType } from '../interfaces/slide-initial-data-service.interface';
import { SlideInitialDataService } from './slide-initial-data-service';
import { DocCreateStatus, IChunksGenerator } from '../interfaces/chunks-generator.interface';
import { migratePresentation } from '@tencent/slide-sdk-data-migration';
import { ILogger, SlideDataServices } from '../slide-data-services.core';
import { Mutation } from '@tencent/slide-sdk-model/src/mutation/base/base-mutation';
import { RevisionData } from '@tencent/slide-sdk-model/src/app/revision-data';
import { CommandData } from '@tencent/slide-sdk-model/src/app/command-data';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import flattenDeep from 'lodash/flattenDeep';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';

export class PresentationStoreService implements IPresentationStoreService {
    private presentation: PresentationDataModel;
    private transaction: ITransactionProvider;

    public constructor(
        private initialDataService: ISlideInitialDataService,
        private contentStatusManager: IContentStatusManager,
        private mutationManager: IMutationManager,
        transactionManager: {},
        private event: IEventManager,
        private docInfoProvider: { globalPadId?: string },
        observerManager: {},
        private logger: ILogger,
        private operationLogger?: OperationLogger,
    ) {
        // transactionManager/observerManager 这里未使用，但需要保留与eventManager配套声明，否则事件通知有问题
        void transactionManager;
        void observerManager;
        this.transaction = createTransactionProvider(this.event);
        this.addEventListeners();

        const { type, data } = this.initialDataService.consume();
        if (type === SlideInitialDataType.PresentationData) {
            this.initFromData(data);
        } else if (type === SlideInitialDataType.CommandChunkData) {
            this.initFromChunksGenerator(data.head, data.chunks);
        } else if (type === SlideInitialDataType.CommandData) {
            this.initFromCommandData(data);
        } else if (type === SlideInitialDataType.OldPresentationData) {
            this.migrateFromOldPresentationData(data);
        } else if (type === SlideInitialDataType.OldPresentationChunkData) {
            // TODO 桌面端本地编辑2.0锁版本后删除这个 if 分支
            this.initialDataService.setData({ type, data });
            this.dispose();
        }
    }

    public getPresentation(): PresentationDataModel {
        if (!this.presentation) {
            throw Error('You should initPresentation before getPresentation');
        }
        return this.presentation;
    }

    public createFromData(data: IPresentationData) {
        const eventManager = new EventManager();
        const slideInitialDataService = new SlideInitialDataService({
            type: SlideInitialDataType.PresentationData,
            data,
        });
        const contentStatusManager = new ContentStatusManager();
        const mutationManager = new MutationManager(eventManager, this.logger, () => this.docInfoProvider);
        const transactionManager = new TransactionManager(eventManager);
        const observerManager = new ObserverManager(eventManager);
        const presentationStore = new PresentationStoreService(
            slideInitialDataService,
            contentStatusManager,
            mutationManager,
            transactionManager,
            eventManager,
            this.docInfoProvider,
            observerManager,
            this.logger,
        );
        return presentationStore;
    }

    public createFromCommandData(commandData: ICommandData) {
        const eventManager = new EventManager();
        const slideInitialDataService = new SlideInitialDataService({
            type: SlideInitialDataType.CommandData,
            data: commandData,
        });
        const contentStatusManager = new ContentStatusManager();
        const mutationManager = new MutationManager(eventManager, this.logger, () => this.docInfoProvider);
        const transactionManager = new TransactionManager(eventManager);
        const observerManager = new ObserverManager(eventManager);
        const presentationStore = new PresentationStoreService(
            slideInitialDataService,
            contentStatusManager,
            mutationManager,
            transactionManager,
            eventManager,
            this.docInfoProvider,
            observerManager,
            this.logger,
        );
        return presentationStore;
    }

    public createFromChunksGenerator(
        headCommand: ICommandData,
        chunksGenerator: IChunksGenerator<{ commands: ICommandData[] }>,
    ) {
        const eventManager = new EventManager();
        const slideInitialDataService = new SlideInitialDataService({
            type: SlideInitialDataType.CommandChunkData,
            data: {
                head: headCommand,
                chunks: chunksGenerator,
            },
        });
        const contentStatusManager = new ContentStatusManager();
        const mutationManager = new MutationManager(eventManager, this.logger, () => this.docInfoProvider);
        const transactionManager = new TransactionManager(eventManager);
        const observerManager = new ObserverManager(eventManager);
        const presentationStore = new PresentationStoreService(
            slideInitialDataService,
            contentStatusManager,
            mutationManager,
            transactionManager,
            eventManager,
            this.docInfoProvider,
            observerManager,
            this.logger,
        );
        return presentationStore;
    }

    public createFromOldPresentationData(data: string) {
        return this.createFromData(migratePresentation(JSON.parse(data)));
    }

    public createFromSlideFileData(data: ISlideFileData & { revisions?: string[] }) {
        return this.createFromCommandData(new SlideFile(data).getCommandData());
    }

    public applyRevisionData(data: string[]) {
        data.forEach((item) => {
            const revisionData = RevisionData.fromBufferOrBase64(item).getRevisionData();
            revisionData.commands?.forEach((command) => {
                this.applyCommandData(command, MutationSource.Collaborator);
            });
        });
    }

    public applyCommandData(command: ICommandData, mutationSource: MutationSource): void {
        try {
            if (!command.mutations?.length) return;
            const mutations = CommandData.fromMutationData(command.mutations).getMutations();
            this.logger.info('[applyCommandData], mutationSource: ', mutationSource);
            // this.logger.debug('[applyCommandData] mutations: ', mutations);
            this.transaction(() => this.mutationManager.enqueue(mutations, mutationSource).apply());
        } catch (e: any) {
            this.logger.error('[applyCommandData] mutationSource: ', mutationSource, 'err', e.toString());
            throw e;
        }
    }

    public applyMutations(mutations: Mutation[], mutationSource: MutationSource): Mutation[] {
        let invertedMutations: Mutation[] = [];
        try {
            this.transaction(() => {
                this.mutationManager.enqueue(mutations, mutationSource).apply();
                invertedMutations = mutations
                    .map(mutation => this.mutationManager.getInvertedMutation(mutation)).reverse()
                    .flat();
            });
        } catch (e: any) {
            this.logger.error('[applyMutation] err: ', e, ', mutationSource: ', mutationSource);
            throw e;
        }
        return invertedMutations;
    }

    public getSerializedPresentationData(presentation = this.presentation.getWritableOwnedData()) {
        return SlideFile.fromPresentationData(presentation).getData();
    }

    public async getSerializedPresentationDataByWorker(presentation = this.presentation.getWritableOwnedData()) {
        if (process.env.WORKER_DISABLE) return this.getSerializedPresentationData();
        const slideFileManager = await SlideDataServices.SlideFileManager.promise();
        const instance = await slideFileManager.recent();
        if (instance) {
            const res = await instance.slideFile
                .fromPresentationData(presentation);
            slideFileManager.recentBack(instance);
            return res.data;
        }
        return this.getSerializedPresentationData();
    }

    public getSerializedPresentationDataWithPage(presentation = this.presentation.getWritableOwnedData()) {
        return SlideFile.fromPresentationData(presentation, true).getData();
    }

    // eslint-disable-next-line max-len
    public async getSerializedPresentationDataWithPageByWorker(presentation = this.presentation.getWritableOwnedData()) {
        if (process.env.WORKER_DISABLE) return this.getSerializedPresentationDataWithPage();
        const slideFileManager = await SlideDataServices.SlideFileManager.promise();
        const instance = await slideFileManager.recent();
        if (instance) {
            const res = await instance.slideFile
                .fromPresentationData(presentation, true);
            slideFileManager.recentBack(instance);
            return res.data;
        }
        return this.getSerializedPresentationDataWithPage();
    }

    public getPresentationKeyFrame() {
        return CommandData.fromValidMutationData(getPresentationKeyframe(this.presentation.store.data))
            .getMutationData();
    }

    public getPresentationSnapShotData() {
        return {
            object: this.getPresentation().getOwnedData(),
        };
    }

    public dispose() {
        this.presentation.dispose();
        this.event.dispose();
    }

    public getMutationManager() {
        return this.mutationManager;
    }

    public getTransaction() {
        return this.transaction;
    }

    private initFromData(data: IPresentationData = this.getDefaultPresentationData()) {
        this.init(data);
        this.contentStatusManager.emitter.emit(ContentStatusEvents.LoadAllChunksSuccess);
        return this;
    }

    private initFromCommandData(commandData: ICommandData) {
        this.init(this.getDefaultPresentationData());
        this.applyCommandData(commandData, MutationSource.Keyframe);
        this.contentStatusManager.emitter.emit(ContentStatusEvents.LoadAllChunksSuccess);
        return this;
    }

    private migrateFromOldPresentationData(data: string) {
        const newPresentation = migratePresentation(JSON.parse(data));
        return this.initFromData(newPresentation);
    }

    private initFromChunksGenerator(
        headCommand: ICommandData,
        chunksGenerator: IChunksGenerator<{ commands: ICommandData[] }>,
    ) {
        this.init(this.getDefaultPresentationData());
        const MAX_BUFFERED_TIME = 16; // ms
        const MAX_BUFFERED_CHUNK = Infinity;
        const MAX_CHUNKS = chunksGenerator.total ?? 65536;
        let bufferedChunks: { commands: ICommandData[] }[] = [{ commands: [headCommand] }];
        let lastCommitTime = 0;

        const handleError = (e) => {
            this.logger.error('[initFromChunksGenerator] load chunk err:', e);
            this.contentStatusManager.emitter.emit(ContentStatusEvents.LoadError, e);
        };

        const commitBufferedChunks = (isHead?: boolean) => {
            if (bufferedChunks.length > 0) {
                this.transaction(() => {
                    const mutations = flattenDeep<IValidMutationData>(bufferedChunks
                        .map(({ commands }) => commands
                            .map(command => CommandData
                                .fromMutationData(command.mutations ?? []).getValidMutationData())));

                    // 找到分片包含的 slide pageId
                    const effectSlideIds = mutations
                        .filter(mut => mut.type === MutationType.SetPageProperties
                            && (mut as IValidSetPagePropertiesMutationData).pageType === STPageType.slide)
                        .map(mut => (mut as IValidSetPagePropertiesMutationData).pageId);
                    this.mutationManager.enqueue(createMutations(mutations), MutationSource.Keyframe);
                    this.mutationManager.apply();
                    this.updateSlideLoadingState(!!isHead, MAX_CHUNKS, chunksGenerator.fullPageCount, effectSlideIds);
                });
                bufferedChunks = [];
            }
        };
        try {
            commitBufferedChunks(true);
        } catch (e) {
            handleError(e);
            return this;
        }
        this.contentStatusManager.emitter.emit(ContentStatusEvents.LoadHeadSuccess);

        const loadChunks = async (): Promise<DocCreateStatus> => {
            let i = 0;

            lastCommitTime = performance.now();
            let docCreateStatus = DocCreateStatus.AlreadyCreate;
            while (i < MAX_CHUNKS) {
                const { done, chunk, docCreateStatus: status } = await chunksGenerator.next();
                if (chunk) {
                    bufferedChunks.push(chunk);
                }

                if (
                    performance.now() - lastCommitTime >= MAX_BUFFERED_TIME
                    || bufferedChunks.length >= MAX_BUFFERED_CHUNK
                    || done
                ) {
                    commitBufferedChunks();
                    await new Promise(resolve => setTimeout(resolve));
                    lastCommitTime = performance.now();
                }

                if (done) {
                    docCreateStatus = status;
                    break;
                }

                i += 1;
                if (i >= MAX_CHUNKS) {
                    throw new Error('Exceed max chunks limit.');
                }
            }
            return docCreateStatus;
        };

        const loadChunksLogic = () => {
            loadChunks()
                .then((docCreateStatus: DocCreateStatus) => {
                    if (docCreateStatus === DocCreateStatus.AlreadyCreate) {
                        this.contentStatusManager.emitter.emit(ContentStatusEvents.LoadAllChunksSuccess);
                    } else {
                        eventBus.on(EventKeys.DOC_CREATED_SUCCESS, () => {
                            this.contentStatusManager.emitter.emit(ContentStatusEvents.LoadAllChunksSuccess);
                        });
                    }
                })
                .catch((e) => {
                    handleError(e);
                });
        };

        if (browser.isMobile) {
            // 不可用VISIBLE_PAGE_DISPLAY，因为一旦滚动出首包页数外，而对应页的真实数据还未请求，则当前屏幕内的页永远不会渲染完成
            eventBus.onIncludingPast(EventKeys.ALL_THUMBNAIL_SITE_COMPLETE, () => {
                loadChunksLogic();
            });
        } else {
            loadChunksLogic();
        }
        return this;
    }

    private getDefaultPresentationData(): IPresentationData {
        return {
            id: new JSF32RandomIDGenerator().next(),
            type: DataType.Presentation,
            version: V3_DEVR,
            slideList: [],
            layoutMap: {},
            noteSlideMap: {},
            notesMasterMap: {},
            masterList: [],
            themeList: [],
            sectionMap: {},
            resourceMap: {
                images: {},
                videos: {},
                audios: {},
                fonts: {},
            },
            sldSz: {
                cx: 12192000,
                cy: 6858000,
                type: STSlideSizeType.screen16x9,
            },
            notesSz: {
                cx: 6858000,
                cy: 9144000,
            },
            showSpecialPlsOnTitleSld: false,
        };
    }

    private init(data: IPresentationData) {
        if (this.presentation) return;
        this.presentation = new PresentationDataModel(data, this.event);
        this.presentation.attach();
        this.mutationManager.registerPresentation(this.presentation);

        if (this.operationLogger?.init) {
            this.operationLogger?.init?.(this.presentation);

            AppServiceProvider.getInstance().waitForReady((appService) => {
                const uiDataService = (appService as IEditorAppServices).getUiDataService?.();
                if (uiDataService) {
                    uiDataService.operationLogger = this.operationLogger;
                }
            });
        }
    }

    private addEventListeners() {
        this.event.on(PresentationEventTypes.Edit, () => {
            this.contentStatusManager.emitter.emit(ContentStatusEvents.EditorOperation);
        });
        this.event.on(CommandEventTypes.UndoRedoChange, ({ source }) => {
            if (source === CommandSource.Redo) {
                this.contentStatusManager.emitter.emit(ContentStatusEvents.EditorRedo);
            } else if (source === CommandSource.Undo) {
                this.contentStatusManager.emitter.emit(ContentStatusEvents.EditorUndo);
            }
        });
    }

    /**
     * 用来标记分页加载中，各个 slide loading 状态的变化
     * @param isHead
     * @param effectSlideIds
     * @returns
     */
    private updateSlideLoadingState(
        isHead: boolean,
        chunkLength: number,
        fullSlideCount: number,
        effectSlideIds: string[],
    ) {
        // 如果只有 1 片，且是 head 片，不需要更新 loading 状态
        if (isHead && chunkLength !== 1) {
            if (!this.getPresentation().slideList.length) return;
            this.getPresentation().slideList
                .forEach((slide, index) => (index <= fullSlideCount - 1
                    ? slide.changeLoadingState(false)
                    : slide.changeLoadingState(true)));
            return;
        }
        effectSlideIds.forEach((slideId) => {
            const slide = this.getPresentation().getSlideById(slideId);
            slide?.changeLoadingState(false);
        });
    }
}
